import { invokeFunction } from './invokeFunction';

/* =============================================================================
 * The second factor, by email
 * -----------------------------------------------------------------------------
 * The other half of core/mfa.ts, which handles the authenticator app through
 * Supabase's own MFA. This one goes through an Edge Function, because GoTrue
 * has no notion of an emailed second factor and cannot mint `aal2` for one.
 *
 * What gets marked instead is the session — see migration 0137 and the
 * mfa-email function. Nothing here needs to know that; it asks for a code and
 * offers one back.
 *
 * Like mfa.ts, this file deliberately carries no prose of its own. The Edge
 * Function answers a refusal with a written explanation — how many tries are
 * left, whether the code expired, whether the company allows this route at all
 * — and a wrapper that replaced those with its own summary would be throwing
 * away the only part a person can act on.
 * ========================================================================== */

export interface CodeSent {
  ok: true;
  expiresInMinutes: number;
}

/**
 * Sends a code to the address on the account.
 *
 * Throttled server-side. A refusal comes back as a thrown Error carrying the
 * server's own sentence, which names the wait.
 */
export async function sendEmailCode(): Promise<CodeSent> {
  return invokeFunction<CodeSent>('mfa-email', { body: { action: 'send' } });
}

/**
 * Offers six digits.
 *
 * Throws with the server's explanation on a wrong code — including how many
 * attempts remain, which is the one thing somebody staring at a rejected code
 * actually wants to know.
 */
export async function verifyEmailCode(code: string): Promise<void> {
  await invokeFunction<{ ok: true }>('mfa-email', {
    body: { action: 'verify', code },
  });
}
